import type { FC, ReactNode } from 'react'

import { useMemo, useState } from 'react'
import { ArrowRight, ChevronDown, Search } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import SiteHeader from '@/components/landing/site-header'
import SiteFooter from '@/components/landing/site-footer'
import ContactForm from '@/components/landing/contact-form'

const PLAY_URL = 'https://play.google.com/store/apps/details?id=works.rarefrequency.ctrl&hl=en_US'
const MS_URL = 'https://apps.microsoft.com/detail/9ntx9m4mtj1l?hl=en-US&gl=US'

const FAQS = [
    { q: 'How do I start with DOCSCRUB?', a: 'Open DOCSCRUB on the web, Android or Windows, pick a tool and open your PDF. We recommend working on a copy of your file the first time.' },
    { q: 'How many free saves do I get?', a: 'The free version includes 3 saves, downloads or exports per day. Premium removes the limit.' },
    { q: 'How do I put a signature or date on a PDF?', a: 'Use Sign Documents. You can draw or type a signature and add the date before saving.' },
    { q: 'Can I change text that is already in my PDF?', a: 'Yes, with Edit Existing Text. Scanned pages are images, so their text cannot be edited directly. Use the OCR Text Scanner to copy the text instead. Complex fonts and layouts are worth checking after you save.' },
    { q: 'What does DOCSCRUB Premium include?', a: 'Unlimited saves and exports, unlimited metadata cleanups, OCR on every page, signatures without the FREE mark, and an ad-free experience where applicable.' },
    { q: 'How much is Premium?', a: 'Premium is $5.99 a month or $39.99 a year, in USD. Taxes and local pricing are shown in the store at checkout.' },
    { q: 'Where do I buy or restore Premium?', a: 'Premium is bought inside the Android app (Google Play) or the Windows app (Microsoft Store). The web version is free and has no Premium. Purchases do not transfer between platforms, so restore through the same store account you bought with.' },
    { q: 'How do I cancel or ask for a refund?', a: 'Manage your subscription in Google Play or the Microsoft Store, wherever you bought it. Refunds follow that store’s rules.' },
    { q: 'Why does my exported PDF look different?', a: 'Keep your original file and check the saved copy. If something looks wrong, send us your device, app version, the tool you used and the steps you took using the form below.' },
    { q: 'Are the image tools free?', a: 'Yes. No account is needed and your images are processed in your browser.' },
    { q: 'Will compression hit exactly 100KB?', a: 'Not always. The compressor aims at or below your target. Very small targets may also need smaller image dimensions.' },
    { q: 'What does metadata removal protect?', a: 'It removes stored details such as camera, GPS or author information. It does not remove anything visible on the page or in the image, and it does not guarantee anonymity.' }
]

const PLATFORMS = [
    { name: 'Web', note: 'Free in your browser. Nothing to install.', href: 'https://app.rarefrequencyworks.com/', cta: 'Open web app' },
    { name: 'Android', note: 'For your phone or tablet.', href: PLAY_URL, cta: 'Get it on Google Play' },
    { name: 'Windows', note: 'PDF work on your desktop.', href: MS_URL, cta: 'Get it from Microsoft' },
    { name: 'Amazon Fire', note: 'For your Fire tablet.', href: 'https://www.amazon.com/gp/product/B0HL4XBLKN', cta: 'Get it on Amazon' }
]

const SupportPage: FC = (): ReactNode => {
    const [query, setQuery] = useState('')

    const results = useMemo(() => {
        const term = query.trim().toLowerCase()
        if (!term) return FAQS
        return FAQS.filter((item) => `${item.q} ${item.a}`.toLowerCase().includes(term))
    }, [query])

    return (
        <Layout>
            <Seo title='Support' description='Answers about DOCSCRUB, Premium, the free image tools and Morpho Radar, plus a form to reach Rare Frequency Works directly.' />

            <div className='min-h-screen bg-[#060b14] text-slate-200'>
                <SiteHeader />

                <section id='hero' className='relative overflow-hidden'>
                    <div className='pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[900px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-3xl' />
                    <div className='relative mx-auto max-w-6xl px-6 pt-20 pb-12 md:pt-28'>
                        <p className='font-mono text-xs tracking-[0.2em] text-cyan-300 uppercase'>Support</p>
                        <h1 className='mt-5 max-w-3xl text-4xl leading-[1.05] font-extrabold tracking-tight text-white md:text-6xl'>
                            How can we <span className='font-serif-accent font-normal text-amber-300'>help?</span>
                        </h1>
                        <p className='mt-6 max-w-2xl text-lg leading-relaxed text-slate-400'>
                            Search the common questions below, or send us a message and we will get back to you by email.
                        </p>
                        <div className='mt-8 flex flex-wrap gap-4'>
                            <a href='#faq' className='inline-flex items-center gap-2 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-bold text-[#06121f] transition hover:bg-cyan-200'>
                                Browse answers
                            </a>
                            <a href='#contact' className='inline-flex items-center gap-2 rounded-xl border border-white/15 px-5 py-3 text-sm font-bold text-white transition hover:border-cyan-300/50'>
                                Contact us
                            </a>
                        </div>
                    </div>
                </section>

                <section id='faq' className='mx-auto max-w-3xl px-6 pb-20'>
                    <h2 className='text-2xl font-extrabold text-white md:text-3xl'>Common questions</h2>
                    <label htmlFor='faq-search' className='sr-only'>Search the answers</label>
                    <div className='relative mt-6'>
                        <Search className='pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-slate-500' />
                        <input
                            id='faq-search'
                            type='search'
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder='Try saves, signature, price or refund'
                            className='w-full rounded-xl border border-white/10 bg-[#0a1424] py-3 pr-4 pl-11 text-sm text-white placeholder:text-slate-500 focus:border-cyan-300/60 focus:outline-none focus:ring-2 focus:ring-cyan-300/20'
                        />
                    </div>
                    <p className='mt-3 text-xs text-slate-500' aria-live='polite'>
                        {results.length} {results.length === 1 ? 'answer' : 'answers'} found
                    </p>

                    <div className='mt-6 divide-y divide-white/10 border-y border-white/10'>
                        {results.map((item) => (
                            <details key={item.q} className='group py-1'>
                                <summary className='flex cursor-pointer list-none items-center justify-between gap-4 py-4 font-semibold text-white'>
                                    {item.q}
                                    <ChevronDown className='size-4 shrink-0 text-slate-400 transition group-open:rotate-180' />
                                </summary>
                                <p className='pb-5 text-sm leading-relaxed text-slate-400'>{item.a}</p>
                            </details>
                        ))}
                        {results.length === 0 && (
                            <p className='py-6 text-sm text-slate-400'>
                                No answers match that search. <a href='#contact' className='text-cyan-300 hover:underline'>Send us a message</a> instead.
                            </p>
                        )}
                    </div>
                </section>

                <section id='contact' className='border-y border-white/10 bg-[#081120]'>
                    <div className='mx-auto grid max-w-6xl gap-10 px-6 py-20 lg:grid-cols-[1fr_1.4fr]'>
                        <div>
                            <h2 className='text-2xl font-extrabold text-white md:text-3xl'>Still stuck? Send us a message</h2>
                            <p className='mt-4 leading-relaxed text-slate-400'>
                                Tell us what you were trying to do and what happened instead. The more detail you share, the faster we can help.
                            </p>
                            <p className='mt-4 text-sm leading-relaxed text-slate-500'>
                                For purchases, include which store you bought from. Never send passwords, payment details or private documents.
                            </p>
                        </div>
                        <div className='rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-8'>
                            <ContactForm />
                        </div>
                    </div>
                </section>

                <section id='platforms' className='mx-auto max-w-6xl px-6 py-20'>
                    <h2 className='text-2xl font-extrabold text-white md:text-3xl'>Get DOCSCRUB</h2>
                    <div className='mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                        {PLATFORMS.map((p) => (
                            <a key={p.name} href={p.href} className='group flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition hover:border-cyan-300/40'>
                                <h3 className='font-bold text-white'>{p.name}</h3>
                                <p className='mt-2 text-sm text-slate-400'>{p.note}</p>
                                <span className='mt-auto inline-flex items-center gap-2 pt-6 text-sm font-semibold text-cyan-300'>
                                    {p.cta} <ArrowRight className='size-4 transition group-hover:translate-x-0.5' />
                                </span>
                            </a>
                        ))}
                    </div>
                </section>

                <section id='radar-help' className='mx-auto max-w-6xl px-6 pb-20'>
                    <div className='flex flex-col items-start justify-between gap-6 rounded-3xl border border-emerald-300/15 bg-gradient-to-b from-[#0b2027] to-[#08161c] p-8 md:flex-row md:items-center md:p-10'>
                        <div>
                            <h2 className='text-xl font-extrabold text-white md:text-2xl'>Using Morpho Radar?</h2>
                            <p className='mt-2 max-w-xl text-sm leading-relaxed text-slate-400'>
                                Market inputs, wallet requirements, payments and API examples are all in the Radar documentation. The free simulator does not need a wallet.
                            </p>
                        </div>
                        <a href='https://radar.rarefrequencyworks.com/docs' className='inline-flex shrink-0 items-center gap-2 rounded-xl bg-emerald-300 px-5 py-3 text-sm font-bold text-[#06121f] transition hover:bg-emerald-200'>
                            Open Radar docs <ArrowRight className='size-4' />
                        </a>
                    </div>
                </section>

                <SiteFooter />
            </div>
        </Layout>
    )
}

export default SupportPage
